import React from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import useDocumentTitle from '../../hooks/useDocumentTitle';
import { useDefaultSearchParams } from '../../hooks/useDefaultSearchParams';

import { TEMPLATES_LIBRARY_TABS } from '../../constants/template';
import { SORT_TYPES, templateLibrarySortOptions } from '../../constants/sort';

import { parseTemplatesLibrarySearchParams } from '../../helpers/parseTemplatesLibrarySearchParams';

import SortControls from '../../components/SortControls/SortControls';
import SearchBarWithUrlParamsHandling from '../../components/SearchBarWithUrlParamsHandling/SearchBarWithUrlParamsHandling';
import TemplatesLibraryPaginatedContainer from './TemplatesLibraryPaginatedContainer/TemplatesLibraryPaginatedContainer';
import { Button, Tabs } from '../../design-system';
import { capitalizeStr } from '../../helpers/formatOrganizationDataForTable';
import { AddCircleLineIcon } from '../../design-system/Icons';

export const createNewTemplateRoute = {
    [TEMPLATES_LIBRARY_TABS.goals]: '/templates/goal/new',
    [TEMPLATES_LIBRARY_TABS.processes]: '/templates/process/new',
    [TEMPLATES_LIBRARY_TABS.playbooks]: '/templates/playbook/new',
};

const templateDetailRoute = {
    [TEMPLATES_LIBRARY_TABS.goals]: '/templates/goals',
    [TEMPLATES_LIBRARY_TABS.processes]: '/templates/processes',
    [TEMPLATES_LIBRARY_TABS.playbooks]: '/templates/playbooks',
};

const tabs = [
    TEMPLATES_LIBRARY_TABS.goals,
    TEMPLATES_LIBRARY_TABS.processes,
    TEMPLATES_LIBRARY_TABS.playbooks,
];
const formattedTabs = tabs.map((tab) => ({ name: capitalizeStr(tab), key: tab }));

const TemplatesLibraryPage = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [searchParams] = useSearchParams();
    const parsedParams = parseTemplatesLibrarySearchParams(searchParams);

    const activeTab = location.pathname?.slice(11); // goals, processes or playbooks
    const tabIndex = tabs.findIndex((tab) => tab === activeTab) || 0;

    useDefaultSearchParams({
        checkPage: true,
        checkSort: true,
        defaultSortType: SORT_TYPES.created,
        availableSortTypes: [SORT_TYPES.created, SORT_TYPES.alphabetical],
    });

    useDocumentTitle('Templates');

    const onTabChanged = ({ key }) => {
        const route = templateDetailRoute[key] || templateDetailRoute[0];
        navigate(route);
    };

    const handleNewTemplateClick = () => {
        navigate(createNewTemplateRoute[activeTab], {
            state: { templatesLibraryLocation: location },
        });
    };

    return (
        <div className="page-position pt-4 px-4 xs:px-5 sm:px-6 pb-8 pb:pb-[40px] md:px-8 md:pt-8 flex flex-col gap-5 bg-neutral-50 overflow-y-auto">
            <div className="flex items-start flex-col">
                <p className="font-heading-bold text-heading-bold-l text-black">Templates</p>
                <p className="font-body text-body-regular-xs text-neutral-300 pt-1">
                    Tested and approved configurations of tasks and goals for use in a process or
                    playbook.
                </p>
            </div>
            <div className="flex min-[910px]:items-center flex-row gap-x-4 gap-y-2 justify-between flex-wrap">
                <div className="flex min-[910px]:items-center gap-2 flex-grow flex-wrap">
                    <div className="w-fit">
                        <Tabs
                            onTabChanged={onTabChanged}
                            tabIndex={tabIndex}
                            tabs={formattedTabs}
                        />
                    </div>
                    <div className="flex items-center gap-2 flex-grow max-sm:relative">
                        <div className="flex-grow xs:min-w-[220px] max-w-[300px]">
                            <SearchBarWithUrlParamsHandling placeholder="Search by name" />
                        </div>

                        <SortControls
                            sortOptions={templateLibrarySortOptions}
                            variant="primary"
                            buttonContainerClassName="sm:relative"
                        />
                    </div>
                </div>
                <div>
                    <Button
                        type="secondary"
                        size="xs"
                        text="New Template"
                        leadingIcon={AddCircleLineIcon}
                        onClick={handleNewTemplateClick}
                    />
                </div>
            </div>

            <TemplatesLibraryPaginatedContainer parsedParams={parsedParams} activeTab={activeTab} />
        </div>
    );
};

export default TemplatesLibraryPage;
